import React, { useEffect, useState } from "react";
import Resources from "../../../types/Resources";
import { changePasswordAsync, getDetailedUserInfoAsync } from "../../../apiClient";
import { showDangerAlert } from "../../../common";

export type ChangePasswordModalProps = {
    show: boolean;
    userId: string;
    resources: Resources;
    onPasswordUpdated: () => void;
    onHide: () => void;
};

function useChangePasswordModal(props: ChangePasswordModalProps) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordError, setPasswordError] = useState<string | undefined>();
    const [confirmPasswordError, setConfirmPasswordError] = useState<string | undefined>();
    const [requestInProgress, setRequestInProgress] = useState(false);

    const disableSubmit = requestInProgress || !password || !confirmPassword;

    useEffect(() => {
        async function fetchEmailAsync() {
            const result = await getDetailedUserInfoAsync(props.userId);
            if (!result.failed) {
                setEmail(result.value.email);
            }
        }

        fetchEmailAsync();
    }, [props.userId]);

    function handlePasswordChange(newValue: string) {
        setPassword(newValue);
    }

    function handleConfirmPasswordChange(newValue: string) {
        setConfirmPassword(newValue);
    }

    async function handleSubmitAsync(e: React.FormEvent) {
        e.preventDefault();

        if (password !== confirmPassword) {
            setPassword("");
            setConfirmPassword("");
            setConfirmPasswordError(props.resources.get("PasswordMissmatchError"));
            return;
        }

        setRequestInProgress(true);
        const result = await changePasswordAsync(props.userId, { password, confirmPassword });
        setRequestInProgress(false);

        if (result.failed) {
            const errors = result.error.errors;
            const passwordFieldError = errors?.["Password"]?.[0];
            const confirmFieldError = errors?.["ConfirmPassword"]?.[0];

            if (!passwordFieldError && !confirmFieldError) {
                showDangerAlert(props.resources.get("ChangePasswordError") ?? result.error.title);
            } else {
                setPasswordError(passwordFieldError);
                setConfirmPasswordError(confirmFieldError);
            }
            
            return;
        }

        setPassword("");
        setConfirmPassword("");
        setPasswordError(undefined);
        setConfirmPasswordError(undefined);
        props.onPasswordUpdated();
    }

    function handleHide() {
        setPassword("");
        setConfirmPassword("");
        setPasswordError(undefined);
        setConfirmPasswordError(undefined);
        props.onHide();
    }

    return {
        email,
        password,
        confirmPassword,
        passwordError,
        confirmPasswordError,
        disableSubmit,
        handlePasswordChange,
        handleConfirmPasswordChange,
        handleSubmitAsync,
        handleHide
    };
}

export default useChangePasswordModal;
